import React, { useState, useEffect, useRef } from 'react';
import { Button, FloatButton, message, Drawer } from 'antd';
import { DownloadOutlined, CloseOutlined, MobileOutlined } from '@ant-design/icons';
import { showInstallPrompt, isInstallable, isPWA, getPWAStatus } from '../utils/pwaInstall';

const PWAInstallPrompt = () => {
  const [visible, setVisible] = useState(false);
  const [installStatus, setInstallStatus] = useState(null);
  const [showPrompt, setShowPrompt] = useState(false);
  const installButtonRef = useRef(null);
  const promptShownRef = useRef(false);

  useEffect(() => {
    const checkInstallStatus = () => {
      const status = getPWAStatus();
      setInstallStatus(status);
      
      // Show install prompt if installable and not already installed
      // and hasn't been shown before
      if (status.isInstallable && !status.isInstalled && !promptShownRef.current) {
        // Delay showing the prompt to avoid immediate popup
        setTimeout(() => {
          setShowPrompt(true);
          promptShownRef.current = true;
        }, 3000);
      }
    };

    // Check initial status
    checkInstallStatus();

    // Listen for PWA events
    const handleInstallable = () => {
      console.log('[PWAInstallPrompt] App became installable');
      checkInstallStatus();
    };

    const handleInstalled = () => {
      console.log('[PWAInstallPrompt] App was installed');
      checkInstallStatus();
      setShowPrompt(false);
      message.success('App installed successfully!');
    };

    window.addEventListener('pwa-installable', handleInstallable);
    window.addEventListener('pwa-installed', handleInstalled);

    // Check status periodically
    const interval = setInterval(checkInstallStatus, 5000);

    return () => {
      window.removeEventListener('pwa-installable', handleInstallable);
      window.removeEventListener('pwa-installed', handleInstalled);
      clearInterval(interval);
    };
  }, []);

  const handleInstallClick = async () => {
    try {
      const installed = await showInstallPrompt();
      if (installed) {
        setShowPrompt(false);
        message.success('App installed successfully!');
      }
    } catch (error) {
      console.error('[PWAInstallPrompt] Install failed:', error);
      message.error('Failed to install app. Please try again.');
    }
  };

  const handleDismiss = () => {
    setShowPrompt(false);
    // Store dismissal in localStorage to not show again for a while
    localStorage.setItem('pwa-prompt-dismissed', Date.now().toString());
  };

  // Don't show if already installed or not installable
  if (!installStatus?.isInstallable || installStatus?.isInstalled) {
    return null;
  }

  // Mobile install prompt
  if (window.innerWidth <= 768) {
    return (
      <>
        {showPrompt && (
          <div style={{
            position: 'fixed',
            bottom: 0,
            left: 0,
            right: 0,
            background: 'linear-gradient(135deg, #1677ff 0%, #4096ff 100%)',
            color: 'white',
            padding: '16px',
            zIndex: 998,
            boxShadow: '0 -4px 20px rgba(0,0,0,0.15)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            gap: '12px',
            // Safe area support
            paddingBottom: 'max(16px, env(safe-area-inset-bottom))'
          }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '12px', flex: 1 }}>
              <MobileOutlined style={{ fontSize: '24px' }} />
              <div>
                <div style={{ fontWeight: 'bold', fontSize: '14px', marginBottom: '2px' }}>
                  Install WeRise App
                </div>
                <div style={{ fontSize: '12px', opacity: 0.9 }}>
                  Get offline access and faster performance
                </div>
              </div>
            </div>
            
            <div style={{ display: 'flex', gap: '8px' }}>
              <Button
                size="small"
                onClick={handleDismiss}
                style={{
                  background: 'rgba(255,255,255,0.2)',
                  border: '1px solid rgba(255,255,255,0.3)',
                  color: 'white',
                  height: '36px',
                  borderRadius: '18px'
                }}
              >
                Later
              </Button>
              <Button
                type="primary"
                size="small"
                icon={<DownloadOutlined />}
                onClick={handleInstallClick}
                style={{
                  background: 'white',
                  color: '#1677ff',
                  border: 'none',
                  height: '36px',
                  borderRadius: '18px',
                  fontWeight: 'bold'
                }}
              >
                Install
              </Button>
            </div>
          </div>
        )}
      </>
    );
  }

  // Desktop install button
  return (
    <>
      <FloatButton
        ref={installButtonRef}
        icon={<DownloadOutlined />}
        type="primary"
        style={{
          right: 24,
          bottom: 80,
          width: 56,
          height: 56,
          fontSize: '24px',
          background: 'linear-gradient(135deg, #1677ff 0%, #4096ff 100%)',
          border: '3px solid white',
          boxShadow: '0 4px 20px rgba(22, 119, 255, 0.4)',
          display: showPrompt ? 'flex' : 'none',
          zIndex: 997
        }}
        onClick={handleInstallClick}
        tooltip="Install WeRise App"
      />
      
      {/* Desktop install drawer */}
      <Drawer
        title={
          <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
            <MobileOutlined style={{ fontSize: '24px', color: '#1677ff' }} />
            <span>Install WeRise App</span>
          </div>
        }
        placement="bottom"
        onClose={handleDismiss}
        open={showPrompt}
        height={200}
        style={{
          borderRadius: '16px 16px 0 0'
        }}
        closable={true}
        extra={
          <Button
            type="primary"
            icon={<DownloadOutlined />}
            onClick={handleInstallClick}
            style={{
              background: 'linear-gradient(135deg, #1677ff 0%, #4096ff 100%)',
              border: 'none',
              borderRadius: '8px',
              fontWeight: 'bold'
            }}
          >
            Install Now
          </Button>
        }
      >
        <div style={{ padding: '16px 0' }}>
          <p style={{ fontSize: '16px', marginBottom: '16px', lineHeight: '1.5' }}>
            Install the WeRise app to your device for:
          </p>
          <ul style={{ 
            listStyle: 'none', 
            padding: 0, 
            margin: 0,
            fontSize: '14px',
            lineHeight: '1.8'
          }}>
            <li style={{ marginBottom: '8px' }}>
              ✅ Offline access to surveys
            </li>
            <li style={{ marginBottom: '8px' }}>
              ✅ Faster performance
            </li>
            <li style={{ marginBottom: '8px' }}>
              ✅ Automatic data synchronization
            </li>
            <li>
              ✅ Native app experience
            </li>
          </ul>
        </div>
      </Drawer>
    </>
  );
};

export default PWAInstallPrompt;